import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl, API_ENDPOINTS, DOMAIN } from '../config/apiConfig';
import { apiHelper } from '../utils/apiHelper';
import styled from 'styled-components';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaSync, FaPlus, FaTrash, FaStar, FaCalendar, FaEdit, FaArrowUp, FaCompress, FaLayerGroup, FaImage } from 'react-icons/fa';

const EditContainer = styled.div`
  padding: 30px 5%;
  min-height: 100vh;
  background-color: #0b0614;
  color: white;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    gap: 15px;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  flex: 1;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
    padding: 10px 20px;
    font-size: 0.9rem;
    font-weight: 600;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;

    &.primary {
        background: var(--gradient-pink);
        color: white;
    }

    &.secondary {
        background: var(--glass-bg);
        color: var(--primary-color);
        border: 1px solid var(--border-color);
    }

    &.success {
        background: #27ae60;
        color: white;
    }

    &:hover:not(:disabled) {
        transform: translateY(-2px);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  background: #1a102a;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  margin-bottom: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  label {
    font-weight: 600;
    color: var(--secondary-color);
    font-size: 0.9rem;
  }
  input, textarea, select {
    padding: 10px 12px;
    font-size: 0.9rem;
    background: #0b0614;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    color: var(--primary-color);
    outline: none;
    transition: border-color 0.3s ease;
    &:focus {
        border-color: var(--gradient-pink);
    }
  }
  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EpisodeManager = styled.div`
  margin-top: 30px;
  h2 { 
    margin-bottom: 15px; 
    font-size: 1.4rem;
    color: var(--primary-color);
  }
`;

const EpisodeHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;
    flex-wrap: wrap;
`;

const CheckNewButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border: 1px solid var(--border-color);
    background: transparent;
    color: var(--secondary-color);
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.8rem;
    transition: all 0.3s ease;
    &:hover {
        color: white;
        border-color: white;
    }
`;

const SeasonTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
`;

const SeasonTab = styled.button`
    padding: 8px 12px;
    border: 1px solid ${props => props.isActive ? 'var(--gradient-pink)' : 'var(--border-color)'};
    background: ${props => props.isActive ? 'var(--gradient-pink)' : 'transparent'};
    color: white;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.8rem;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const EpisodeForm = styled.div`
    background: #1a102a;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 12px;
    border-left: 3px solid var(--gradient-cyan);
`;

const EpisodeThumbnail = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;
    padding: 10px;
    background: #0b0614;
    border-radius: 6px;
    border: 1px solid var(--border-color);

    img {
        width: 120px;
        height: 67px;
        object-fit: cover;
        border-radius: 4px;
        border: 1px solid var(--border-color);
    }

    .thumbnail-info {
        flex: 1;
        
        small {
            color: var(--secondary-color);
            font-size: 0.7rem;
        }
    }
`;

const LanguageSection = styled.div`
    margin-top: 12px;
    padding: 12px;
    background: #0b0614;
    border-radius: 6px;
    border: 1px solid var(--border-color);
`;

const LanguageTitle = styled.h5`
    margin-bottom: 8px;
    color: var(--secondary-color);
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const AddLanguageButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: transparent;
    border: 1px dashed var(--border-color);
    color: var(--secondary-color);
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    margin-bottom: 8px;

    &:hover {
        border-color: var(--gradient-cyan);
        color: var(--gradient-cyan);
    }
`;

const LanguageInputGroup = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    align-items: center;

    input {
        flex: 1;
        padding: 6px 10px;
        background: #1a102a;
        border: 1px solid var(--border-color);
        border-radius: 4px;
        color: white;
        font-size: 0.8rem;
    }

    button {
        padding: 4px 8px;
        background: #e74c3c;
        border: none;
        border-radius: 4px;
        color: white;
        cursor: pointer;
        font-size: 0.7rem;
    }
`;

const SEOSection = styled.div`
    margin-top: 15px;
    padding: 15px;
    background: #0b0614;
    border-radius: 8px;
    border: 1px solid var(--gradient-cyan);
`;

const SEOTitle = styled.h5`
    margin-bottom: 12px;
    color: var(--gradient-cyan);
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const SEOPreview = styled.div`
    margin-top: 15px;
    padding: 15px;
    background: #1a102a;
    border-radius: 6px;
    border: 1px solid var(--border-color);
`;

const PreviewTitle = styled.div`
    color: #1a73e8;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 4px;
    text-decoration: none;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

const PreviewUrl = styled.div`
    color: #0d652d;
    font-size: 0.85rem;
    margin-bottom: 8px;
`;

const PreviewDescription = styled.div`
    color: #5f6368;
    font-size: 0.9rem;
    line-height: 1.4;
`;

const CharacterCount = styled.small`
    color: var(--secondary-color);
    font-size: 0.75rem;
    margin-top: 4px;
    display: block;
    
    &.warning {
        color: #ffa500;
    }
    
    &.error {
        color: #ff4757;
    }
`;

const FansubInputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;
    padding: 8px;
    background: #1a102a;
    border-radius: 4px;
    border: 1px solid var(--border-color);
`;

const FansubRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;

    input {
        padding: 6px 10px;
        background: #0b0614;
        border: 1px solid var(--border-color);
        border-radius: 4px;
        color: white;
        font-size: 0.8rem;

        &.fansub-name {
            flex: 1;
            max-width: 150px;
        }
        &.fansub-iframe {
            flex: 2;
        }
    }

    button {
        padding: 4px 8px;
        background: #e74c3c;
        border: none;
        border-radius: 4px;
        color: white;
        cursor: pointer;
        font-size: 0.7rem;
    }
`;

const AlternateTitlesSection = styled.div`
    margin-top: 20px;
    padding: 15px;
    background: #1a102a;
    border-radius: 8px;
    border: 1px solid var(--border-color);
`;

const AlternateTitleInput = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 8px;

    input {
        flex: 1;
        padding: 8px;
        background: #0b0614;
        border: 1px solid var(--border-color);
        border-radius: 4px;
        color: white;
        font-size: 0.8rem;
    }

    button {
        padding: 8px 12px;
        background: #e74c3c;
        border: none;
        border-radius: 4px;
        color: white;
        cursor: pointer;
        font-size: 0.7rem;
    }
`;

const AddTitleButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: transparent;
    border: 1px dashed var(--border-color);
    color: var(--secondary-color);
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8rem;

    &:hover {
        border-color: var(--gradient-cyan);
        color: var(--gradient-cyan);
    }
`;

const TitleList = styled.div`
    margin-top: 10px;
    
    div {
        padding: 6px 10px;
        background: #0b0614;
        margin-bottom: 4px;
        border-radius: 4px;
        border: 1px solid var(--border-color);
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.8rem;
    }

    span {
        flex: 1;
    }

    .title-actions {
        display: flex;
        gap: 5px;
    }

    .make-main-btn {
        padding: 4px 8px;
        background: #27ae60;
        border: none;
        border-radius: 4px;
        color: white;
        cursor: pointer;
        font-size: 0.7rem;
        display: flex;
        align-items: center;
        gap: 3px;
    }
`;

const CategoriesSection = styled.div`
    margin-top: 20px;
    padding: 15px;
    background: #1a102a;
    border-radius: 8px;
    border: 1px solid var(--border-color);
`;

const CategoryInput = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 8px;

    input {
        flex: 1;
        padding: 8px;
        background: #0b0614;
        border: 1px solid var(--border-color);
        border-radius: 4px;
        color: white;
        font-size: 0.8rem;
    }

    button {
        padding: 8px 12px;
        background: #e74c3c;
        border: none;
        border-radius: 4px;
        color: white;
        cursor: pointer;
        font-size: 0.7rem;
    }
`;

const CategoryList = styled.div`
    margin-top: 10px;
    
    div {
        padding: 6px 10px;
        background: #0b0614;
        margin-bottom: 4px;
        border-radius: 4px;
        border: 1px solid var(--border-color);
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.8rem;
    }

    span {
        flex: 1;
    }
`;

const AddSeasonButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 15px;
    background: var(--gradient-cyan);
    border: none;
    border-radius: 6px;
    color: #111;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.8rem;
    margin-bottom: 15px;

    &:hover {
        transform: translateY(-2px);
    }
`;

const ManualEpisodeForm = styled.div`
    background: #1a102a;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 12px;
    border-left: 3px solid var(--gradient-pink);
`;

const DeleteButton = styled.button`
    padding: 4px 8px;
    background: #e74c3c;
    border: none;
    border-radius: 4px;
    color: white;
    cursor: pointer;
    font-size: 0.7rem;
    margin-left: 8px;
`;

const BulkActions = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
    flex-wrap: wrap;
`;

const BulkActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #8e44ad;
    border: none;
    border-radius: 6px;
    color: white;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 500;

    &:hover {
        transform: translateY(-2px);
    }
`;

const AddEpisodeForm = styled.div`
    background: #1a102a;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 12px;
    border-left: 3px solid #f39c12;
`;

const LoadingText = styled.div`
    text-align: center;
    padding: 50px;
    color: var(--secondary-color);
    font-size: 1.2rem;
`;

const ErrorText = styled.div`
    text-align: center;
    padding: 50px;
    color: #e94057;
    font-size: 1.2rem;
`;

const AdminEditPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [content, setContent] = useState(null);
    const [seasons, setSeasons] = useState([]);
    const [episodes, setEpisodes] = useState([]);
    const [alternateTitles, setAlternateTitles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeSeason, setActiveSeason] = useState(1);
    const [loading, setLoading] = useState(true);
    const [loadingEpisodes, setLoadingEpisodes] = useState(false);
    const [newAlternateTitle, setNewAlternateTitle] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [showFansubInput, setShowFansubInput] = useState(false);
    const [newFansub, setNewFansub] = useState({ name: '', iframe: '' });
    const [manualEpisodeNumber, setManualEpisodeNumber] = useState('');
    const [addEpisodeData, setAddEpisodeData] = useState({ season: '', episode: '' });
    const [newSeasonNumber, setNewSeasonNumber] = useState('');
    const [saving, setSaving] = useState(false);
    const [savingSEO, setSavingSEO] = useState(false);

    // Debounce için state
    const [saveTimeout, setSaveTimeout] = useState(null);

    // MySQL'den içerik verilerini çek
    useEffect(() => {
        const fetchContent = async () => {
            setLoading(true);
            try {
                // İçerik bilgilerini çek
                const contentResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_CONTENT)}?id=${id}`);
                if (!contentResponse.ok) throw new Error('İçerik çekilemedi');
                const contentData = await contentResponse.json();
                
                // Japonca karakter tespiti ve title/original_title swap kontrolü
                // Eğer anime ise ve title Japonca karakterler içeriyorsa, original_title ile swap et
                let swapped = false;
                if (contentData.media_type === 'anime' || contentData.media_type === 'tv') {
                    const title = contentData.title || '';
                    const originalTitle = contentData.original_title || '';
                    
                    // Japonca karakter regex: Hiragana, Katakana, Kanji
                    const japaneseRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/;
                    
                    // İngilizce (Latin karakterler) kontrolü - sadece Latin karakterler ve boşluk/noktalama
                    const isEnglish = (str) => {
                        if (!str || str.trim() === '') return false;
                        // Sadece Latin karakterler, rakamlar, boşluk ve yaygın noktalama işaretleri
                        return /^[a-zA-Z0-9\s\-'.,!?()&:;]+$/.test(str);
                    };
                    
                    // Eğer title Japonca karakterler içeriyorsa ve original_title İngilizce ise, swap et
                    if (japaneseRegex.test(title) && isEnglish(originalTitle) && originalTitle.trim() !== '') {
                        // Swap yap
                        const temp = contentData.title;
                        contentData.title = contentData.original_title;
                        contentData.original_title = temp;
                        swapped = true;
                    }
                }
                
                setContent(contentData);
                
                // Eğer swap yapıldıysa otomatik olarak kaydet
                if (swapped && contentData.id) {
                    try {
                        await updateContent('contents', {
                            title: contentData.title,
                            original_title: contentData.original_title
                        }, { id: contentData.id });
                        console.log('Title/original_title swap otomatik olarak kaydedildi.');
                    } catch (error) {
                        console.error('Swap kaydedilirken hata:', error);
                    }
                }

                // Sezonları çek
                const seasonsResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${id}`);
                if (seasonsResponse.ok) {
                    const seasonsData = await seasonsResponse.json();
                    setSeasons(seasonsData);

                    // İlk sezonu aktif yap ve bölümleri çek
                    if (seasonsData.length > 0) {
                        setActiveSeason(seasonsData[0].season_number);
                        fetchSeasonEpisodes(seasonsData[0].id);
                    } else if (contentData.media_type === 'movie') {
                        // Film için otomatik sezon ve episode oluştur
                        await createMovieEpisode(contentData.id, contentData.title);
                    }
                }

                // Alternatif başlıkları çek
                const titlesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_ALTERNATE_TITLES)}?content_id=${id}`);
                if (titlesResponse.ok) {
                    const titlesData = await titlesResponse.json();
                    // API'den gelen veriyi normalize et - string array'e çevir
                    let titles = [];
                    if (Array.isArray(titlesData)) {
                        titles = titlesData.map(t => {
                            // Eğer object ise title property'sini al, değilse direkt kullan
                            if (typeof t === 'string') return t;
                            if (t && typeof t === 'object') {
                                return t.title || t.name || t.alternate_title || String(t);
                            }
                            return String(t);
                        }).filter(t => t && typeof t === 'string' && t.trim() !== '');
                    }
                    setAlternateTitles(titles);
                }

                // Kategorileri çek
                const categoriesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_CATEGORIES)}?content_id=${id}`);
                if (categoriesResponse.ok) {
                    const categoriesData = await categoriesResponse.json();
                    // API direkt array döndürüyor veya {success: true, categories: [...]} formatında
                    if (Array.isArray(categoriesData)) {
                        // Direkt array ise, name property'sini çıkar
                        setCategories(categoriesData.map(cat => cat.name || cat.category_name || cat));
                    } else if (categoriesData.success && Array.isArray(categoriesData.categories)) {
                        // {success: true, categories: [...]} formatında ise
                        setCategories(categoriesData.categories.map(cat => cat.name || cat.category_name || cat));
                    } else {
                        setCategories([]);
                    }
                }

            } catch (error) {
                console.error('Veri çekme hatası:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, [id]);

        const getImageUrl = (path, size = 'w500') => {
        // Path kontrolü - boş, null, undefined veya sadece whitespace ise null döndür
        if (!path || path === 'null' || path === 'undefined' || (typeof path === 'string' && path.trim() === '')) {
            return null; // null döndür, placeholder'ı component'te göster
        }
        
        // String'e çevir ve trim yap
        path = String(path).trim();
        
        // Zaten tam URL ise direkt döndür
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }
        
        // Zaten proxy URL'si ise direkt döndür
        if (path.includes('/proxy-image.php')) {
            return path;
        }
        
        // Zaten data URI ise direkt döndür
        if (path.startsWith('data:')) {
            return path;
        }
        
        // Eğer path tam bir TMDB URL'si ise, sadece path kısmını çıkar
        if (path.includes('image.tmdb.org')) {
            const match = path.match(/\/t\/p\/[^/]+\/(.+)$/);
            if (match) {
                path = '/' + match[1];
            }
        }
        
        // Path'i normalize et - başında / olmalı (http/https ile başlamıyorsa)
        let normalizedPath = path;
        if (!normalizedPath.startsWith('/') && !normalizedPath.startsWith('http')) {
            normalizedPath = '/' + normalizedPath;
        }
        
        // Proxy URL oluştur
        return `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(normalizedPath)}&size=${size}`;
        };

    // MySQL'e güncelleme fonksiyonu
    const updateContent = async (table, data, where) => {
        try {
            // Güvenlik: ID ve timestamp kolonlarını data'dan filtrele
            const forbiddenColumns = ['id', 'content_id', 'season_id', 'user_id', 'episode_id', 'comment_id', 'created_at', 'updated_at'];
            const filteredData = { ...data };
            forbiddenColumns.forEach(col => {
                delete filteredData[col];
            });
            
            const response = await apiHelper.updateContent({ table, data: filteredData, where });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Güncelleme başarısız' }));
                throw new Error(errorData.error || 'Güncelleme başarısız');
            }
            
            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Güncelleme hatası:', error);
            throw error;
        }
    };

    // SEO değerlerini otomatik oluştur
    const generateSEOForEpisode = useCallback((episode) => {
        if (!episode || !content) return {};
        
        const seoData = {};
        const contentTitle = content.title || '';
        const seasonNum = episode.season_number || activeSeason || 1;
        const episodeNum = episode.episode_number || 1;
        const episodeTitle = episode.episode_title || '';
        const episodeOverview = episode.episode_overview || '';
        
        // Alternatif başlıkları keywords'e ekle
        // String kontrolü ekle - sadece string olanları al
        const allTitles = [
          contentTitle,
          ...alternateTitles
        ].filter(t => {
          // String kontrolü ve boş olmayan kontrolü
          return typeof t === 'string' && t.trim() !== '';
        });
        const keywordsParts = [];
        
        allTitles.forEach(title => {
            keywordsParts.push(`${title} izle`);
            keywordsParts.push(`${title} sezon ${seasonNum} bölüm ${episodeNum} izle`);
            keywordsParts.push(`${title} türkçe izle`);
            keywordsParts.push(`${title} sezon ${seasonNum} bölüm ${episodeNum} türkçe izle`);
            keywordsParts.push(`${title} türkçe dublaj`);
            keywordsParts.push(`${title} türkçe altyazı`);
        });
        
        const keywordsString = keywordsParts.join(', ');
        
        // SEO Title - sadece boşsa oluştur
        if (!episode.seo_title || episode.seo_title.trim() === '') {
            seoData.seo_title = `${contentTitle} Sezon ${seasonNum} Bölüm ${episodeNum} Türkçe İzle`;
        }
        
        // SEO Description - sadece boşsa oluştur
        if (!episode.seo_description || episode.seo_description.trim() === '') {
            const overviewText = episodeOverview ? episodeOverview.substring(0, 120) : '';
            seoData.seo_description = `${contentTitle} Sezon ${seasonNum} Bölüm ${episodeNum} Türkçe dublaj ve altyazılı olarak izleyin. ${episodeTitle ? episodeTitle + '. ' : ''}${overviewText} Ücretsiz ve HD kalitede.`.trim();
        }
        
        // SEO Keywords - sadece boşsa oluştur (alternatif başlıklarla birlikte)
        if (!episode.seo_keywords || episode.seo_keywords.trim() === '') {
            seoData.seo_keywords = keywordsString;
        }
        
        // Canonical URL - sadece boşsa oluştur
        if (!episode.seo_canonical_url || episode.seo_canonical_url.trim() === '') {
            const mediaType = content.media_type === 'anime' ? 'tv' : (content.media_type || 'tv');
            seoData.seo_canonical_url = `${DOMAIN}/watch/${mediaType}/${id}/season/${seasonNum}/episode/${episodeNum}`;
        }
        
        return seoData;
    }, [content, activeSeason, id, alternateTitles]);

    // Bölüm çekme fonksiyonu
    const fetchSeasonEpisodes = useCallback(async (seasonId) => {
        setLoadingEpisodes(true);
        try {
            const response = await fetch(`${getApiUrl(API_ENDPOINTS.GET_EPISODES)}?season_id=${seasonId}`);
            if (response.ok) {
                const episodesData = await response.json();
                
                // SEO alanları boşsa otomatik doldur ve kaydet (hemen kaydet, debounce yok)
                const episodesWithSEO = [];
                for (const episode of episodesData) {
                    const seoData = generateSEOForEpisode(episode);
                    const needsUpdate = Object.keys(seoData).length > 0;
                    
                    if (needsUpdate) {
                        // Veritabanına hemen kaydet (debounce yok)
                        try {
                            await updateContent('episodes', seoData, { id: episode.id });
                            episodesWithSEO.push({ ...episode, ...seoData });
                        } catch (error) {
                            console.error(`Bölüm ${episode.id} SEO kaydedilirken hata:`, error);
                            episodesWithSEO.push(episode);
                        }
                    } else {
                        episodesWithSEO.push(episode);
                    }
                }
                
                setEpisodes(episodesWithSEO);
            }
        } catch (error) {
            console.error('Bölümler çekilirken hata:', error);
        } finally {
            setLoadingEpisodes(false);
        }
    }, [generateSEOForEpisode]);

    // Sezon değiştiğinde bölümleri çek
    useEffect(() => {
        const activeSeasonData = seasons.find(s => s.season_number === activeSeason);
        if (activeSeasonData) {
            fetchSeasonEpisodes(activeSeasonData.id);
        }
    }, [activeSeason, seasons, fetchSeasonEpisodes]);

    // Debounce ile input değişikliklerini yönet
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setContent(prev => ({ ...prev, [name]: value }));
        
        // Eğer şu anda kaydetme işlemi yapılıyorsa auto-save'i bekle
        if (saving) return;
        
        // Yasak kolonları kontrol et
        const forbiddenColumns = ['id', 'content_id', 'season_id', 'user_id', 'episode_id', 'comment_id', 'created_at', 'updated_at'];
        if (forbiddenColumns.includes(name)) {
            return; // Yasak kolonları güncelleme
        }
        
        // Debounce: 1 saniye bekledikten sonra kaydet
        if (saveTimeout) clearTimeout(saveTimeout);
        const timeout = setTimeout(() => {
            updateContent('contents', { [name]: value }, { id: content.id }).catch(err => {
                console.error('Auto-save hatası:', err);
            });
        }, 1000);
        setSaveTimeout(timeout);
    };

    const handleEpisodeChange = async (episodeId, field, value) => {
        // Local state'i hemen güncelle
        let updatedEpisode = null;
        setEpisodes(prev => {
            const updated = prev.map(ep => {
                if (ep.id === episodeId) {
                    const newEp = { ...ep, [field]: value };
                    updatedEpisode = newEp;
                    return newEp;
                }
                return ep;
            });
            
            // Eğer episode_title, episode_overview, episode_number gibi alanlar değiştiyse ve SEO alanları boşsa otomatik doldur
            if (['episode_title', 'episode_overview', 'episode_number'].includes(field) && updatedEpisode) {
                const seoData = generateSEOForEpisode(updatedEpisode);
                Object.keys(seoData).forEach(seoField => {
                    if (!updatedEpisode[seoField] || updatedEpisode[seoField].trim() === '') {
                        updatedEpisode[seoField] = seoData[seoField];
                    }
                });
                // SEO alanlarını da state'e ekle
                return updated.map(ep => 
                    ep.id === episodeId ? { ...ep, ...seoData } : ep
                );
            }
            
            return updated;
        });

        // Debounce: 1 saniye bekledikten sonra kaydet
        if (saveTimeout) clearTimeout(saveTimeout);
        const timeout = setTimeout(async () => {
            // Güncel episode'u al
            const currentEpisode = updatedEpisode || episodes.find(ep => ep.id === episodeId);
            const updateData = { [field]: value };
            
            // Eğer SEO alanları boşsa otomatik doldur ve hemen kaydet
            if (currentEpisode) {
                const seoData = generateSEOForEpisode(currentEpisode);
                Object.keys(seoData).forEach(seoField => {
                    if (!currentEpisode[seoField] || currentEpisode[seoField].trim() === '') {
                        updateData[seoField] = seoData[seoField];
                    }
                });
            }
            
            // SEO alanları varsa hemen kaydet (debounce beklemeden)
            if (Object.keys(updateData).length > 1) {
                try {
                    await updateContent('episodes', updateData, { id: episodeId });
                    // Local state'i de güncelle
                    setEpisodes(prev => prev.map(ep => 
                        ep.id === episodeId ? { ...ep, ...updateData } : ep
                    ));
                } catch (error) {
                    console.error('SEO kaydetme hatası:', error);
                }
            } else {
                // Sadece normal alan değişikliği ise normal kaydet
                await updateContent('episodes', updateData, { id: episodeId });
            }
            
            // Takvimi yenile
            window.dispatchEvent(new CustomEvent('episodeUpdated'));
        }, 1000);
        setSaveTimeout(timeout);
    };


    // Bölüm kapağı güncelleme
    const handleEpisodeThumbnailChange = async (episodeId, thumbnailPath) => {
        await handleEpisodeChange(episodeId, 'thumbnail_path', thumbnailPath);
    };

    // Yeni bölüm ekleme
    const addEpisode = async (seasonId, episodeNumber) => {
        if (!episodeNumber) {
            alert('Bölüm numarası gerekli!');
            return;
        }

        // Önce aynı bölüm numarasının olup olmadığını kontrol et
        const existingEpisode = episodes.find(ep => ep.episode_number == episodeNumber);
        if (existingEpisode) {
            alert(`Bölüm ${episodeNumber} zaten Sezon ${activeSeason}'da var!`);
            return;
        }

        try {
            const response = await apiHelper.addEpisode({
                season_id: seasonId,
                episode_number: episodeNumber,
                episode_title: '',
                episode_overview: '',
                thumbnail_path: '',
                iframe_code: ''
            });
            
            if (response.status === 409) {
                alert(`Bölüm ${episodeNumber} zaten Sezon ${activeSeason}'da var!`);
                return;
            }
            
            const result = await response.json();
            if (result.success) {
                // Bölümleri yeniden çek
                const activeSeasonData = seasons.find(s => s.season_number === activeSeason);
                if (activeSeasonData) {
                    fetchSeasonEpisodes(activeSeasonData.id);
                }
                setManualEpisodeNumber('');
                alert('Bölüm başarıyla eklendi!');
                // Takvimi yenile
                window.dispatchEvent(new CustomEvent('episodeUpdated'));
            } else {
                alert(`Bölüm eklenirken hata: ${result.message || 'Bilinmeyen hata'}`);
            }
        } catch (error) {
            console.error('Bölüm ekleme hatası:', error);
            if (error.message.includes('409')) {
                alert(`Bölüm ${episodeNumber} zaten mevcut!`);
            } else {
                alert('Bölüm eklenirken hata oluştu!');
            }
        }
    };

    // Bölüm silme
    const deleteEpisode = async (episodeId, seasonId) => {
        if (window.confirm('Bu bölümü silmek istediğinizden emin misiniz?')) {
            try {
                const response = await apiHelper.deleteEpisode({ id: episodeId });
                
                const result = await response.json();
                if (result.success) {
                    // Bölümleri yeniden çek
                    fetchSeasonEpisodes(seasonId);
                    alert('Bölüm başarıyla silindi!');
                } else {
                    alert('Bölüm silinirken hata oluştu!');
                }
            } catch (error) {
                console.error('Bölüm silme hatası:', error);
                alert('Bölüm silinirken hata oluştu!');
            }
        }
    };

    // Yeni sezon ekleme
    const addSeason = async () => {
        if (!newSeasonNumber) {
            alert('Sezon numarası gerekli!');
            return;
        }

        try {
            const response = await apiHelper.addSeason({
                content_id: content.id,
                season_number: parseInt(newSeasonNumber)
            });
            
            const result = await response.json();
            if (result.success) {
                // Sezonları yeniden çek
                const seasonsResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${content.id}`);
                if (seasonsResponse.ok) {
                    const seasonsData = await seasonsResponse.json();
                    setSeasons(seasonsData);
                }
                setNewSeasonNumber('');
                alert('Sezon başarıyla eklendi!');
            } else {
                alert('Sezon eklenirken hata oluştu!');
            }
        } catch (error) {
            console.error('Sezon ekleme hatası:', error);
            alert('Sezon eklenirken hata oluştu!');
        }
    };

    // Sezon silme
    const deleteSeason = async (seasonId, seasonNumber) => {
        if (window.confirm(`Sezon ${seasonNumber} ve tüm bölümlerini silmek istediğinizden emin misiniz?`)) {
            try {
                const response = await apiHelper.deleteSeason({ id: seasonId });
                
                const result = await response.json();
                if (result.success) {
                    // Sezonları yeniden çek
                    const seasonsResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${content.id}`);
                    if (seasonsResponse.ok) {
                        const seasonsData = await seasonsResponse.json();
                        setSeasons(seasonsData);
                        
                        // Aktif sezonu güncelle
                        if (seasonsData.length > 0) {
                            setActiveSeason(seasonsData[0].season_number);
                        } else {
                            setEpisodes([]);
                        }
                    }
                    alert('Sezon başarıyla silindi!');
                } else {
                    alert('Sezon silinirken hata oluştu!');
                }
            } catch (error) {
                console.error('Sezon silme hatası:', error);
                alert('Sezon silinirken hata oluştu!');
            }
        }
    };

    // Tüm sezonları birleştir
    const mergeAllSeasons = async () => {
        if (window.confirm('Tüm sezonları 1. sezonda birleştirmek istediğinizden emin misiniz? Bu işlem geri alınamaz!')) {
            try {
                const response = await apiHelper.mergeSeasons({ content_id: content.id });
                
                const result = await response.json();
                if (result.success) {
                    // Sezonları ve bölümleri yeniden çek
                    const seasonsResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${content.id}`);
                    if (seasonsResponse.ok) {
                        const seasonsData = await seasonsResponse.json();
                        setSeasons(seasonsData);
                        
                        if (seasonsData.length > 0) {
                            setActiveSeason(1);
                            fetchSeasonEpisodes(seasonsData[0].id);
                        }
                    }
                    alert('Tüm sezonlar başarıyla birleştirildi!');
                } else {
                    alert('Sezonlar birleştirilirken hata oluştu!');
                }
            } catch (error) {
                console.error('Sezon birleştirme hatası:', error);
                alert('Sezonlar birleştirilirken hata oluştu!');
            }
        }
    };

    // Alternatif başlık işlemleri
    const addAlternateTitle = async () => {
        if (newAlternateTitle.trim() && !alternateTitles.includes(newAlternateTitle.trim())) {
            try {
                const response = await apiHelper.addAlternateTitle({
                    content_id: content.id,
                    title: newAlternateTitle.trim()
                });
                
                const result = await response.json();
                if (result.success) {
                    setAlternateTitles(prev => [...prev, newAlternateTitle.trim()]);
                    setNewAlternateTitle('');
                }
            } catch (error) {
                console.error('Alternatif başlık ekleme hatası:', error);
            }
        }
    };

    const removeAlternateTitle = async (titleToRemove) => {
        try {
            const response = await apiHelper.deleteAlternateTitle({
                content_id: content.id,
                title: titleToRemove
            });
            
            const result = await response.json();
            if (result.success) {
                setAlternateTitles(prev => prev.filter(title => title !== titleToRemove));
            }
        } catch (error) {
            console.error('Alternatif başlık silme hatası:', error);
        }
    };

    // Kategori işlemleri
    const addCategory = async () => {
        const categoryName = newCategory.trim();
        if (categoryName && !categories.includes(categoryName)) {
            try {
                const response = await apiHelper.addCategory({
                    content_id: content.id,
                    category_name: categoryName
                });
                
                const result = await response.json();
                if (result.success) {
                    // Kategorileri yeniden çek
                    const categoriesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_CATEGORIES)}?content_id=${content.id}`);
                    if (categoriesResponse.ok) {
                        const categoriesData = await categoriesResponse.json();
                        if (Array.isArray(categoriesData)) {
                            setCategories(categoriesData.map(cat => cat.name || cat.category_name || cat));
                        } else if (categoriesData.success && Array.isArray(categoriesData.categories)) {
                            setCategories(categoriesData.categories.map(cat => cat.name || cat.category_name || cat));
                        }
                    }
                    setNewCategory('');
                }
            } catch (error) {
                console.error('Kategori ekleme hatası:', error);
            }
        }
    };

    const removeCategory = async (categoryToRemove) => {
        try {
            const response = await apiHelper.deleteCategory({
                content_id: content.id,
                category_name: categoryToRemove
            });
            
            const result = await response.json();
            if (result.success) {
                // Kategorileri yeniden çek
                const categoriesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_CATEGORIES)}?content_id=${content.id}`);
                if (categoriesResponse.ok) {
                    const categoriesData = await categoriesResponse.json();
                    if (Array.isArray(categoriesData)) {
                        setCategories(categoriesData.map(cat => cat.name || cat.category_name || cat));
                    } else if (categoriesData.success && Array.isArray(categoriesData.categories)) {
                        setCategories(categoriesData.categories.map(cat => cat.name || cat.category_name || cat));
                    }
                }
            }
        } catch (error) {
            console.error('Kategori silme hatası:', error);
        }
    };

    // TMDB'den bölüm detaylarını çek (AdminAddPage mantığına göre)
    const fetchSeasonDetailsFromTMDB = async (tvId, seasonNumber) => {
        try {
            const res = await fetch(`${getApiUrl(API_ENDPOINTS.FETCH_SEASON)}?id=${tvId}&season=${seasonNumber}`);
            if (!res.ok) throw new Error('Sezon detayları çekilemedi');
            return res.json();
        } catch (error) {
            console.error('Sezon detay çekme hatası:', error);
            throw error;
        }
    };

    // TV show detaylarını çek
    const fetchTVDetailsFromTMDB = async (tvId) => {
        try {
            const res = await fetch(`${getApiUrl(API_ENDPOINTS.FETCH_DETAILS)}?id=${tvId}&type=tv`);
            if (!res.ok) throw new Error('TV detayları çekilemedi');
            return res.json();
        } catch (error) {
            console.error('TV detay çekme hatası:', error);
            throw error;
        }
    };

    const checkAllEpisodesFromTMDB = async () => {
        if (!content.tmdb_id) {
            alert('TMDB ID bulunamadı!');
            return;
        }

        setLoadingEpisodes(true);
        try {
            const details = await fetchTVDetailsFromTMDB(content.tmdb_id);
            const totalSeasons = details.number_of_seasons || 1;
            
            let episodes_added = 0;
            let episodes_updated = 0;

            for (let seasonNum = 1; seasonNum <= totalSeasons; seasonNum++) {
                try {
                    const seasonData = await fetchSeasonDetailsFromTMDB(content.tmdb_id, seasonNum);

                    const today = new Date();
                    today.setHours(23, 59, 59, 999);
                    const releasedEpisodes = seasonData.episodes.filter(ep => 
                        ep.air_date && new Date(ep.air_date) <= today
                    );

                    if (releasedEpisodes.length === 0) continue;

                    // Sezonun veritabanında var olup olmadığını kontrol et
                    // Sezon kontrol kısmını komple değiştir:
                    let seasonResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${content.id}`);
                    let seasonsData = await seasonResponse.json();

                    // SEZON NUMARALARINI NUMBER'A ÇEVİR - BU ÇOK ÖNEMLİ!
                    seasonsData = seasonsData.map(season => ({
                        ...season,
                        season_number: parseInt(season.season_number),
                        id: parseInt(season.id),
                        content_id: parseInt(season.content_id)
                    }));

                    let season_record = seasonsData.find(s => s.season_number === seasonNum);
                    let season_id = null;

                    if (!season_record) {
                        try {
                            const createSeasonResponse = await fetch(getApiUrl(API_ENDPOINTS.ADD_SEASON), {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    content_id: parseInt(content.id),
                                    season_number: parseInt(seasonNum)
                                })
                            });
                            const createResult = await createSeasonResponse.json();
                            
                            if (createResult.success) {
                                seasonResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${content.id}`);
                                seasonsData = await seasonResponse.json();
                                seasonsData = seasonsData.map(season => ({
                                    ...season,
                                    season_number: parseInt(season.season_number),
                                    id: parseInt(season.id)
                                }));
                                
                                season_record = seasonsData.find(s => s.season_number === seasonNum);
                                season_id = season_record?.id;
                            }
                        } catch (error) {
                            console.error(`Sezon ${seasonNum} oluşturma hatası:`, error);
                        }
                    } else {
                        season_id = season_record.id;
                    }

                    if (!season_id) {
                        continue;
                    }

                    const episodesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_EPISODES)}?season_id=${season_id}`);
                    const existingEpisodes = await episodesResponse.json();

                    for (const episodeData of releasedEpisodes) {
                        const existingEpisode = existingEpisodes.find(ep => 
                            parseInt(ep.episode_number) === parseInt(episodeData.episode_number)
                        );
                        
                    if (existingEpisode) {
                        let needsUpdate = false;
                        const updatePayload = {};
                        
                        const hasValidThumbnail = episodeData.still_path && 
                                                episodeData.still_path.trim() !== '' && 
                                                episodeData.still_path !== null;
                        
                        const currentThumbnail = existingEpisode.thumbnail_path;
                        const needsThumbnailUpdate = (!currentThumbnail || 
                                                    currentThumbnail.trim() === '' || 
                                                    currentThumbnail === null) && 
                                                    hasValidThumbnail;
                        
                        if (needsThumbnailUpdate) {
                            updatePayload.thumbnail_path = episodeData.still_path;
                            needsUpdate = true;
                        }
                        
                        const fieldsToCheck = [
                            { 
                                field: 'episode_title', 
                                tmdb: episodeData.name, 
                                current: existingEpisode.episode_title,
                                compare: (a, b) => a !== b
                            },
                            { 
                                field: 'episode_overview', 
                                tmdb: episodeData.overview, 
                                current: existingEpisode.episode_overview,
                                compare: (a, b) => a !== b
                            },
                            { 
                                field: 'air_date', 
                                tmdb: episodeData.air_date, 
                                current: existingEpisode.air_date,
                                compare: (a, b) => {
                                    // null, undefined, boş string kontrolü
                                    const aVal = a || null;
                                    const bVal = b || null;
                                    return aVal !== bVal;
                                }
                            },
                            { 
                                field: 'vote_average', 
                                tmdb: episodeData.vote_average, 
                                current: existingEpisode.vote_average,
                                compare: (a, b) => parseFloat(a || 0) !== parseFloat(b || 0)
                            },
                            { 
                                field: 'vote_count', 
                                tmdb: episodeData.vote_count, 
                                current: existingEpisode.vote_count,
                                compare: (a, b) => parseInt(a || 0) !== parseInt(b || 0)
                            }
                        ];

                        fieldsToCheck.forEach(({ field, tmdb, current, compare }) => {
                            // air_date için özel kontrol: TMDB'de varsa mutlaka güncelle
                            if (field === 'air_date') {
                                // TMDB'den air_date geliyorsa ve mevcut değer null/boş ise veya farklıysa güncelle
                                if (tmdb !== undefined && tmdb !== null && tmdb !== '') {
                                    // Mevcut değer null, undefined, boş string ise veya farklıysa mutlaka güncelle
                                    const currentVal = current || null;
                                    const tmdbVal = tmdb || null;
                                    if (currentVal !== tmdbVal) {
                                        updatePayload[field] = tmdb;
                                        needsUpdate = true;
                                    }
                                }
                            } else {
                                // Diğer alanlar için normal kontrol
                                if (tmdb !== undefined && compare(tmdb, current)) {
                                    updatePayload[field] = tmdb;
                                    needsUpdate = true;
                                }
                            }
                        });
                        
                        if (needsUpdate) {
                            try {
                                // SEO alanlarını da kontrol et ve ekle
                                const seoData = generateSEOForEpisode({ ...existingEpisode, ...updatePayload });
                                const finalUpdatePayload = { ...updatePayload, ...seoData };
                                
                                await updateContent('episodes', finalUpdatePayload, { id: existingEpisode.id });
                                episodes_updated++;
                            } catch (error) {
                                console.error('Episode update error:', error);
                            }
                        } else {
                            // Güncelleme yoksa bile SEO alanlarını kontrol et
                            const seoData = generateSEOForEpisode(existingEpisode);
                            if (Object.keys(seoData).length > 0) {
                                try {
                                    await updateContent('episodes', seoData, { id: existingEpisode.id });
                                } catch (error) {
                                    console.error('SEO update error:', error);
                                }
                            }
                        }
                        } else {
                            const newEpisode = {
                                season_id: season_id,
                                episode_number: episodeData.episode_number,
                                episode_title: episodeData.name || `Bölüm ${episodeData.episode_number}`,
                                episode_overview: episodeData.overview || '',
                                thumbnail_path: (episodeData.still_path && episodeData.still_path.trim() !== '') ? episodeData.still_path : '',
                                air_date: episodeData.air_date || null,
                                vote_average: episodeData.vote_average || 0,
                                vote_count: episodeData.vote_count || 0,
                                season_number: seasonNum
                            };
                            
                            // SEO alanlarını oluştur
                            const seoData = generateSEOForEpisode(newEpisode);
                            const episodeWithSEO = { ...newEpisode, ...seoData };
                            
                            const addEpisodeResponse = await fetch(getApiUrl(API_ENDPOINTS.ADD_EPISODE), {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(episodeWithSEO)
                            });
                            const addResult = await addEpisodeResponse.json();
                            if (addResult.success) {
                                episodes_added++;
                            }
                        }
                    }
                } catch (err) {
                    console.error(`Sezon ${seasonNum} hatası:`, err);
                }
            }

            alert(`${episodes_added} bölüm eklendi, ${episodes_updated} bölüm güncellendi!`);
            
            // Sezonları ve bölümleri yeniden çek
            const seasonsResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${content.id}`);
            if (seasonsResponse.ok) {
                const seasonsData = await seasonsResponse.json();
                setSeasons(seasonsData);
                
                // Aktif sezonu güncelle
                if (seasonsData.length > 0) {
                    const activeSeasonData = seasonsData.find(s => s.season_number === activeSeason) || seasonsData[0];
                    setActiveSeason(activeSeasonData.season_number);
                    fetchSeasonEpisodes(activeSeasonData.id);
                }
            }
            
            // Takvimi yenile
            window.dispatchEvent(new CustomEvent('episodeUpdated'));

        } catch (error) {
            console.error('❌ TMDB bölüm kontrol hatası:', error);
            alert('Bölümler kontrol edilirken hata oluştu: ' + error.message);
        } finally {
            setLoadingEpisodes(false);
        }
    };

    // TMDB'den sadece yeni bölümleri kontrol et (bugün ve dün için)
    const checkNewEpisodesFromTMDB = async () => {
        if (!content.tmdb_id) {
            alert('TMDB ID bulunamadı!');
            return;
        }

        setLoadingEpisodes(true);
        try {
            const details = await fetchTVDetailsFromTMDB(content.tmdb_id);
            const totalSeasons = details.number_of_seasons || 1;
            
            let episodes_added = 0;
            let episodes_updated = 0;

            // Bugün ve dünün tarihlerini hesapla
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            for (let seasonNum = 1; seasonNum <= totalSeasons; seasonNum++) {
                try {
                    const seasonData = await fetchSeasonDetailsFromTMDB(content.tmdb_id, seasonNum);
                    
                    // Bugün ve dün yayınlanan bölümleri filtrele
                    const newEpisodes = seasonData.episodes.filter(ep => {
                        if (!ep.air_date) return false;
                        const airDate = new Date(ep.air_date);
                        airDate.setHours(0, 0, 0, 0);
                        const todayTime = today.getTime();
                        const yesterdayTime = yesterday.getTime();
                        const airDateTime = airDate.getTime();
                        return (airDateTime === todayTime || airDateTime === yesterdayTime);
                    });

                    if (newEpisodes.length === 0) continue;

                    // Mevcut sezonları çek
                    let seasonResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${content.id}`);
                    let seasonsData = await seasonResponse.json();
                    
                    // SEZON NUMARALARINI NUMBER'A ÇEVİR
                    seasonsData = seasonsData.map(season => ({
                        ...season,
                        season_number: parseInt(season.season_number),
                        id: parseInt(season.id),
                        content_id: parseInt(season.content_id)
                    }));

                    let season_record = seasonsData.find(s => s.season_number === seasonNum);
                    let season_id = null;

                    if (!season_record) {
                        try {
                            const createSeasonResponse = await fetch(getApiUrl(API_ENDPOINTS.ADD_SEASON), {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    content_id: parseInt(content.id),
                                    season_number: parseInt(seasonNum)
                                })
                            });
                            const createResult = await createSeasonResponse.json();
                            
                            if (createResult.success) {
                                seasonResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${content.id}`);
                                seasonsData = await seasonResponse.json();
                                seasonsData = seasonsData.map(season => ({
                                    ...season,
                                    season_number: parseInt(season.season_number),
                                    id: parseInt(season.id)
                                }));
                                
                                season_record = seasonsData.find(s => s.season_number === seasonNum);
                                season_id = season_record?.id;
                            }
                        } catch (error) {
                            console.error(`Sezon ${seasonNum} oluşturma hatası:`, error);
                        }
                    } else {
                        season_id = season_record.id;
                    }

                    if (!season_id) {
                        continue;
                    }

                    // Mevcut bölümleri çek
                    const episodesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_EPISODES)}?season_id=${season_id}`);
                    const existingEpisodes = await episodesResponse.json();

                    // Yeni bölümleri ekle veya güncelle
                    for (const episodeData of newEpisodes) {
                        const existingEpisode = existingEpisodes.find(ep => 
                            parseInt(ep.episode_number) === parseInt(episodeData.episode_number)
                        );

                        if (existingEpisode) {
                            // Mevcut bölümü güncelle (air_date dahil)
                            let needsUpdate = false;
                            const updatePayload = {};
                            
                            const hasValidThumbnail = episodeData.still_path && 
                                                    episodeData.still_path.trim() !== '' && 
                                                    episodeData.still_path !== null;
                            
                            const currentThumbnail = existingEpisode.thumbnail_path;
                            const needsThumbnailUpdate = (!currentThumbnail || 
                                                        currentThumbnail.trim() === '' || 
                                                        currentThumbnail === null) && 
                                                        hasValidThumbnail;
                            
                            if (needsThumbnailUpdate) {
                                updatePayload.thumbnail_path = episodeData.still_path;
                                needsUpdate = true;
                            }
                            
                            const fieldsToCheck = [
                                { 
                                    field: 'episode_title', 
                                    tmdb: episodeData.name, 
                                    current: existingEpisode.episode_title,
                                    compare: (a, b) => a !== b
                                },
                                { 
                                    field: 'episode_overview', 
                                    tmdb: episodeData.overview, 
                                    current: existingEpisode.episode_overview,
                                    compare: (a, b) => a !== b
                                },
                                { 
                                    field: 'air_date', 
                                    tmdb: episodeData.air_date, 
                                    current: existingEpisode.air_date,
                                    compare: (a, b) => {
                                        // null, undefined, boş string kontrolü
                                        const aVal = a || null;
                                        const bVal = b || null;
                                        return aVal !== bVal;
                                    }
                                },
                                { 
                                    field: 'vote_average', 
                                    tmdb: episodeData.vote_average, 
                                    current: existingEpisode.vote_average,
                                    compare: (a, b) => parseFloat(a || 0) !== parseFloat(b || 0)
                                },
                                { 
                                    field: 'vote_count', 
                                    tmdb: episodeData.vote_count, 
                                    current: existingEpisode.vote_count,
                                    compare: (a, b) => parseInt(a || 0) !== parseInt(b || 0)
                                }
                            ];

                            fieldsToCheck.forEach(({ field, tmdb, current, compare }) => {
                                // air_date için özel kontrol: TMDB'de varsa mutlaka güncelle
                                if (field === 'air_date') {
                                    // TMDB'den air_date geliyorsa ve mevcut değer null/boş ise veya farklıysa güncelle
                                    if (tmdb !== undefined && tmdb !== null && tmdb !== '') {
                                        // Mevcut değer null, undefined, boş string ise veya farklıysa mutlaka güncelle
                                        const currentVal = current || null;
                                        const tmdbVal = tmdb || null;
                                        if (currentVal !== tmdbVal) {
                                            updatePayload[field] = tmdb;
                                            needsUpdate = true;
                                        }
                                    }
                                } else {
                                    // Diğer alanlar için normal kontrol
                                    if (tmdb !== undefined && compare(tmdb, current)) {
                                        updatePayload[field] = tmdb;
                                        needsUpdate = true;
                                    }
                                }
                            });
                            
                            if (needsUpdate) {
                                try {
                                    // SEO alanlarını da kontrol et ve ekle
                                    const seoData = generateSEOForEpisode({ ...existingEpisode, ...updatePayload });
                                    const finalUpdatePayload = { ...updatePayload, ...seoData };
                                    
                                    await updateContent('episodes', finalUpdatePayload, { id: existingEpisode.id });
                                    episodes_updated++;
                                } catch (error) {
                                    console.error('Episode update error:', error);
                                }
                            } else {
                                // Güncelleme yoksa bile SEO alanlarını kontrol et
                                const seoData = generateSEOForEpisode(existingEpisode);
                                if (Object.keys(seoData).length > 0) {
                                    try {
                                        await updateContent('episodes', seoData, { id: existingEpisode.id });
                                    } catch (error) {
                                        console.error('SEO update error:', error);
                                    }
                                }
                            }
                        } else {
                            // Yeni bölüm ekle
                            const newEpisode = {
                                season_id: season_id,
                                episode_number: episodeData.episode_number,
                                episode_title: episodeData.name || `Bölüm ${episodeData.episode_number}`,
                                episode_overview: episodeData.overview || '',
                                thumbnail_path: (episodeData.still_path && episodeData.still_path.trim() !== '') ? episodeData.still_path : '',
                                air_date: episodeData.air_date || null,
                                vote_average: episodeData.vote_average || 0,
                                vote_count: episodeData.vote_count || 0,
                                season_number: seasonNum
                            };
                            
                            // SEO alanlarını oluştur
                            const seoData = generateSEOForEpisode(newEpisode);
                            const episodeWithSEO = { ...newEpisode, ...seoData };
                            
                            const addEpisodeResponse = await fetch(getApiUrl(API_ENDPOINTS.ADD_EPISODE), {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(episodeWithSEO)
                            });
                            const addResult = await addEpisodeResponse.json();
                            if (addResult.success) {
                                episodes_added++;
                            }
                        }
                    }
                } catch (err) {
                    console.log(`Sezon ${seasonNum} hatası:`, err);
                }
            }

            if (episodes_added > 0 || episodes_updated > 0) {
                alert(`${episodes_added} yeni bölüm eklendi, ${episodes_updated} bölüm güncellendi!`);
                
                // Sezonları ve bölümleri yeniden çek
                const seasonsResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${content.id}`);
                if (seasonsResponse.ok) {
                    const seasonsData = await seasonsResponse.json();
                    setSeasons(seasonsData);
                    
                    // Aktif sezonu güncelle
                    if (seasonsData.length > 0) {
                        const activeSeasonData = seasonsData.find(s => s.season_number === activeSeason) || seasonsData[0];
                        setActiveSeason(activeSeasonData.season_number);
                        fetchSeasonEpisodes(activeSeasonData.id);
                    }
                }
                
                // Takvimi yenile
                window.dispatchEvent(new CustomEvent('episodeUpdated'));
            } else {
                alert('Bugün ve dün için yeni bölüm bulunamadı.');
            }

        } catch (error) {
            console.error('TMDB yeni bölüm kontrol hatası:', error);
            alert('Yeni bölümler kontrol edilirken hata oluştu: ' + error.message);
        } finally {
            setLoadingEpisodes(false);
        }
    };

    // Film için otomatik episode oluşturma fonksiyonu
    const createMovieEpisode = async (contentId, title) => {
        try {
            // Önce sezon 1 oluştur
            const seasonResponse = await fetch(getApiUrl(API_ENDPOINTS.ADD_SEASON), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    content_id: contentId,
                    season_number: 1
                })
            });
            
            // Alternatif başlıkları çek
            const titlesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_ALTERNATE_TITLES)}?content_id=${contentId}`);
            let movieAlternateTitles = [];
            if (titlesResponse.ok) {
                const titlesData = await titlesResponse.json();
                movieAlternateTitles = Array.isArray(titlesData) ? titlesData : [];
            }
            
            const seasonResult = await seasonResponse.json();
            if (seasonResult.success) {
                // Sezonları yeniden çek
                const seasonsResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${contentId}`);
                const seasonsData = await seasonsResponse.json();
                setSeasons(seasonsData);
                
                if (seasonsData.length > 0) {
                    // SEO alanlarını oluştur (film için)
                    const allTitles = [title, ...movieAlternateTitles].filter(t => t && t.trim() !== '');
                    const keywordsParts = [];
                    
                    allTitles.forEach(t => {
                        keywordsParts.push(`${t} izle`);
                        keywordsParts.push(`${t} türkçe izle`);
                        keywordsParts.push(`${t} türkçe dublaj`);
                        keywordsParts.push(`${t} türkçe altyazı`);
                    });
                    
                    const seoData = {
                        seo_title: `${title} Türkçe İzle`,
                        seo_description: `${title} Türkçe dublaj ve altyazılı olarak izleyin. Ücretsiz ve HD kalitede.`,
                        seo_keywords: keywordsParts.join(', '),
                        seo_canonical_url: `${DOMAIN}/watch/movie/${contentId}`
                    };
                    
                    // Episode 1 oluştur (SEO alanlarıyla birlikte)
                    const episodeResponse = await fetch(getApiUrl(API_ENDPOINTS.ADD_EPISODE), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            season_id: seasonsData[0].id,
                            episode_number: 1,
                            episode_title: title,
                            episode_overview: '',
                            thumbnail_path: '',
                            iframe_code: '',
                            ...seoData
                        })
                    });
                    
                    const episodeResult = await episodeResponse.json();
                    if (episodeResult.success) {
                        fetchSeasonEpisodes(seasonsData[0].id);
                    }
                }
            }
        } catch (error) {
            console.error('Film episode oluşturma hatası:', error);
        }
    };

    // Basit kaydetme butonu - Sadece içeriği kaydet
    const handleSave = async () => {
        if (saving) return; // Çoklu tıklamayı engelle
        
        setSaving(true);
        try {
            // Auto-save timeout'unu iptal et (çift kayıt önleme)
            if (saveTimeout) {
                clearTimeout(saveTimeout);
                setSaveTimeout(null);
            }
            
            // Sadece güncellenebilir kolonları gönder - tüm yasak kolonları filtrele
            const forbiddenColumns = ['id', 'content_id', 'season_id', 'user_id', 'episode_id', 'comment_id', 'created_at', 'updated_at'];
            const updateableContent = {};
            Object.keys(content).forEach(key => {
                if (!forbiddenColumns.includes(key)) {
                    updateableContent[key] = content[key];
                }
            });
            
            await updateContent('contents', updateableContent, { id: content.id });
            alert("İçerik başarıyla güncellendi!");
        } catch (error) {
            console.error('Kaydetme hatası:', error);
            alert(error.message || "Güncelleme sırasında hata oluştu!");
        } finally {
            setSaving(false);
        }
    };

    // SEO Ayarlarını Kaydet - Tüm sezondaki episode'ları güncelle (sadece değişiklik varsa)
    const handleSaveSEO = async () => {
        if (savingSEO) return; // Çoklu tıklamayı engelle
        
        if (!(content.media_type === 'tv' || content.media_type === 'anime') || seasons.length === 0) {
            alert("Bu içerik türü için SEO güncellemesi yapılamaz.");
            return;
        }

        setSavingSEO(true);
        try {
            let totalUpdated = 0;
            let totalChecked = 0;
            
            for (const season of seasons) {
                try {
                    const episodesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_EPISODES)}?season_id=${season.id}`);
                    if (episodesResponse.ok) {
                        const episodesData = await episodesResponse.json();
                        
                        for (const episode of episodesData) {
                            totalChecked++;
                            // SEO alanları boşsa otomatik doldur
                            const seoData = generateSEOForEpisode({ ...episode, season_number: season.season_number });
                            
                            // Sadece değişiklik varsa kaydet
                            if (Object.keys(seoData).length > 0) {
                                try {
                                    await updateContent('episodes', seoData, { id: episode.id });
                                    totalUpdated++;
                                } catch (error) {
                                    console.error(`Bölüm ${episode.id} SEO kaydedilirken hata:`, error);
                                }
                            }
                        }
                    }
                } catch (seasonError) {
                    console.error(`Sezon ${season.season_number} işlenirken hata:`, seasonError);
                }
            }
            
            if (totalUpdated > 0) {
                alert(`${totalUpdated} bölüm için SEO alanları güncellendi. (${totalChecked} bölüm kontrol edildi)`);
            } else {
                alert(`Tüm bölümler zaten güncel. (${totalChecked} bölüm kontrol edildi)`);
            }
        } catch (error) {
            console.error('SEO kaydetme hatası:', error);
            alert("SEO güncellemesi sırasında hata oluştu!");
        } finally {
            setSavingSEO(false);
        }
    };

    if (loading) return <LoadingText>İçerik yükleniyor...</LoadingText>;
    if (!content) return <ErrorText>İçerik bulunamadı.</ErrorText>;

    return (
        <EditContainer>
            <Header>
                <Title>"{content.title}" Düzenle</Title>
                <HeaderButtons>
                    <ActionButton className="secondary" as={Link} to="/admin">
                        Panele Dön
                    </ActionButton>
                    <ActionButton 
                        className="primary" 
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                    </ActionButton>
                    {(content.media_type === 'tv' || content.media_type === 'anime') && seasons.length > 0 && (
                        <ActionButton 
                            className="success" 
                            onClick={handleSaveSEO}
                            disabled={savingSEO}
                        >
                            {savingSEO ? 'Kaydediliyor...' : 'SEO Ayarlarını Kaydet'}
                        </ActionButton>
                    )}
                </HeaderButtons>
            </Header>

            <Form>
                <InputGroup>
                    <label>Başlık</label>
                    <input type="text" name="title" value={content.title || ''} onChange={handleInputChange} />
                </InputGroup>
                
                <Grid2Col>
                    <InputGroup>
                        <label>Orijinal Başlık</label>
                        <input type="text" name="original_title" value={content.original_title || ''} onChange={handleInputChange} />
                    </InputGroup>
                    <InputGroup>
                        <label>Medya Türü</label>
                        <select name="media_type" value={content.media_type || ''} onChange={handleInputChange}>
                            <option value="movie">Film</option>
                            <option value="tv">Dizi</option>
                            <option value="anime">Anime</option>
                        </select>
                    </InputGroup>
                </Grid2Col>

                <Grid3Col>
                    <InputGroup>
                        <label>Çıkış Tarihi</label>
                        <input 
                            type="date" 
                            name="release_date" 
                            value={content.release_date || ''} 
                            onChange={handleInputChange} 
                        />
                    </InputGroup>
                    <InputGroup>
                        <label>IMDB Puanı</label>
                        <input 
                            type="number" 
                            step="0.1"
                            min="0"
                            max="10"
                            name="vote_average" 
                            value={content.vote_average || ''} 
                            onChange={handleInputChange} 
                        />
                    </InputGroup>
                    <InputGroup>
                        <label>TMDB ID</label>
                        <input 
                            type="number" 
                            name="tmdb_id" 
                            value={content.tmdb_id || ''} 
                            onChange={handleInputChange} 
                        />
                    </InputGroup>
                </Grid3Col>

                <InputGroup>
                    <label>Açıklama</label>
                    <textarea name="overview" value={content.overview || ''} onChange={handleInputChange}></textarea>
                </InputGroup>

                <Grid2Col>
                    <InputGroup>
                        <label>Poster Path</label>
                        <input type="text" name="poster_path" value={content.poster_path || ''} onChange={handleInputChange} />
                    </InputGroup>
                    <InputGroup>
                        <label>Backdrop Path</label>
                        <input type="text" name="backdrop_path" value={content.backdrop_path || ''} onChange={handleInputChange} />
                    </InputGroup>
                </Grid2Col>
            </Form>

            {/* Alternatif Başlıklar Bölümü */}
            <AlternateTitlesSection>
                <h3>Alternatif Başlıklar</h3>
                <p style={{ color: 'var(--secondary-color)', marginBottom: '12px', fontSize: '0.8rem' }}>
                    Arama sonuçlarında görünecek alternatif başlıklar
                </p>
                
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <input 
                        type="text"
                        placeholder="Yeni alternatif başlık ekle..."
                        value={newAlternateTitle}
                        onChange={(e) => setNewAlternateTitle(e.target.value)}
                        style={{ 
                            flex: 1, 
                            padding: '8px', 
                            background: '#0b0614', 
                            border: '1px solid var(--border-color)', 
                            borderRadius: '4px', 
                            color: 'white',
                            fontSize: '0.8rem'
                        }}
                    />
                    <AddTitleButton onClick={addAlternateTitle}>
                        <FaPlus /> Ekle
                    </AddTitleButton>
                </div>

                <TitleList>
                    {alternateTitles.map((title, index) => (
                        <div key={index}>
                            <span>{title}</span>
                            <div className="title-actions">
                                <button onClick={() => removeAlternateTitle(title)}>
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))}
                </TitleList>
            </AlternateTitlesSection>

            {/* Kategoriler Bölümü */}
            <CategoriesSection>
                <h3>Kategoriler</h3>
                <p style={{ color: 'var(--secondary-color)', marginBottom: '12px', fontSize: '0.8rem' }}>
                    İçeriğin kategorilerini ekleyin
                </p>
                
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <input 
                        type="text"
                        placeholder="Yeni kategori ekle..."
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        style={{ 
                            flex: 1, 
                            padding: '8px', 
                            background: '#0b0614', 
                            border: '1px solid var(--border-color)', 
                            borderRadius: '4px', 
                            color: 'white',
                            fontSize: '0.8rem'
                        }}
                    />
                    <AddTitleButton onClick={addCategory}>
                        <FaPlus /> Ekle
                    </AddTitleButton>
                </div>

                <CategoryList>
                    {Array.isArray(categories) && categories.map((category, index) => (
                        <div key={index}>
                            <span>{category}</span>
                            <button onClick={() => removeCategory(category)}>
                                <FaTrash />
                            </button>
                        </div>
                    ))}
                </CategoryList>
            </CategoriesSection>

            {/* TV ve Anime için bölüm yönetimi */}
            {(content.media_type === 'tv' || content.media_type === 'anime') && (
                <EpisodeManager>
                    <EpisodeHeader>
                        <h2>Bölüm Yönetimi</h2>
                        <CheckNewButton onClick={() => checkAllEpisodesFromTMDB()}>
                            <FaSync /> Tüm Bölümleri Kontrol Et
                        </CheckNewButton>
                        <CheckNewButton onClick={() => checkNewEpisodesFromTMDB()}>
                            <FaPlus /> Yeni Bölümleri Kontrol Et
                        </CheckNewButton>
                    </EpisodeHeader>

                    {/* Toplu İşlemler */}
                    <BulkActions>
                        <BulkActionButton onClick={mergeAllSeasons}>
                            <FaCompress /> Tüm Sezonları Birleştir
                        </BulkActionButton>
                        <BulkActionButton onClick={async () => {
                            if (!window.confirm('Tüm bölümler için SEO alanlarını otomatik doldurmak istediğinize emin misiniz?')) return;
                            
                            try {
                                setLoadingEpisodes(true);
                                const activeSeasonData = seasons.find(s => s.season_number === activeSeason);
                                if (activeSeasonData) {
                                    const response = await fetch(`${getApiUrl(API_ENDPOINTS.GET_EPISODES)}?season_id=${activeSeasonData.id}`);
                                    if (response.ok) {
                                        const episodesData = await response.json();
                                        let updatedCount = 0;
                                        
                                        for (const episode of episodesData) {
                                            const seoData = generateSEOForEpisode(episode);
                                            if (Object.keys(seoData).length > 0) {
                                                try {
                                                    await updateContent('episodes', seoData, { id: episode.id });
                                                    updatedCount++;
                                                } catch (error) {
                                                    console.error(`Bölüm ${episode.id} SEO kaydedilirken hata:`, error);
                                                }
                                            }
                                        }
                                        
                                        // Bölümleri yeniden çek
                                        await fetchSeasonEpisodes(activeSeasonData.id);
                                        alert(`${updatedCount} bölüm için SEO alanları otomatik dolduruldu!`);
                                    }
                                }
                            } catch (error) {
                                console.error('SEO doldurma hatası:', error);
                                alert('SEO alanları doldurulurken hata oluştu!');
                            } finally {
                                setLoadingEpisodes(false);
                            }
                        }}>
                            <FaSync /> SEO Alanlarını Doldur
                        </BulkActionButton>
                    </BulkActions>

                    {/* Yeni Sezon Ekleme */}
                    <AddEpisodeForm>
                        <h4>Yeni Sezon Ekle</h4>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                            <input 
                                type="number"
                                placeholder="Sezon No"
                                value={newSeasonNumber}
                                onChange={(e) => setNewSeasonNumber(e.target.value)}
                                style={{ 
                                    padding: '6px', 
                                    background: '#0b0614', 
                                    border: '1px solid var(--border-color)', 
                                    borderRadius: '4px', 
                                    color: 'white',
                                    fontSize: '0.8rem',
                                    width: '100px'
                                }}
                            />
                            <AddTitleButton onClick={addSeason}>
                                <FaPlus /> Sezon Ekle
                            </AddTitleButton>
                        </div>
                    </AddEpisodeForm>

                    {/* Sezon Tabları */}
                    <SeasonTabs>
                        {seasons.map(season => (
                            <SeasonTab 
                                key={season.id} 
                                isActive={season.season_number === activeSeason} 
                                onClick={() => setActiveSeason(season.season_number)}
                            >
                                Sezon {season.season_number}
                                <DeleteButton onClick={(e) => {
                                    e.stopPropagation();
                                    deleteSeason(season.id, season.season_number);
                                }}>
                                    <FaTrash />
                                </DeleteButton>
                            </SeasonTab>
                        ))}
                    </SeasonTabs>

                    {/* Bölüm Ekleme Formu */}
                    <AddEpisodeForm>
                        <h4>Yeni Bölüm Ekle</h4>
                        <p style={{ 
                            color: 'var(--secondary-color)', 
                            fontSize: '0.75rem', 
                            opacity: 0.8, 
                            margin: '0 0 10px 0' 
                        }}>
                            Sezon {activeSeason}'a eklenecek
                        </p>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                            <input 
                                type="number"
                                placeholder="Bölüm No"
                                value={manualEpisodeNumber}
                                onChange={(e) => setManualEpisodeNumber(e.target.value)}
                                style={{ 
                                    padding: '6px', 
                                    background: '#0b0614', 
                                    border: '1px solid var(--border-color)', 
                                    borderRadius: '4px', 
                                    color: 'white',
                                    fontSize: '0.8rem',
                                    width: '100px'
                                }}
                            />
                            <AddTitleButton onClick={() => {
                                const activeSeasonData = seasons.find(s => s.season_number === activeSeason);
                                if (activeSeasonData && manualEpisodeNumber) {
                                    addEpisode(activeSeasonData.id, manualEpisodeNumber);
                                }
                            }}>
                                <FaPlus /> Bölüm Ekle
                            </AddTitleButton>
                        </div>
                    </AddEpisodeForm>

                    {/* Bölüm Listesi */}
                    <div>
                        {loadingEpisodes ? (
                            <LoadingText>Bölümler yükleniyor...</LoadingText>
                        ) : (
                            episodes.map(episode => {
                                const activeSeasonData = seasons.find(s => s.season_number === activeSeason);
                                return (
                                    <EpisodeForm key={episode.id}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <h4>Sezon {activeSeason} - Bölüm {episode.episode_number}</h4>
                                            <DeleteButton onClick={() => deleteEpisode(episode.id, activeSeasonData.id)}>
                                                <FaTrash /> Sil
                                            </DeleteButton>
                                        </div>
                                        
                                        {/* Bölüm Kapağı */}
                                        <EpisodeThumbnail>
                                            {episode.thumbnail_path && episode.thumbnail_path.trim() !== '' ? (
                                                <img 
                                                src={getImageUrl(episode.thumbnail_path, 'w300')} 
                                                alt={`Sezon ${activeSeason} Bölüm ${episode.episode_number}`}
                                                onError={(e) => {
                                                    // Eğer zaten data URI ise, tekrar deneme (sonsuz döngüyü önle)
                                                    if (e.target.src.startsWith('data:')) {
                                                        e.target.onerror = null;
                                                        return;
                                                    }
                                                    // Hata durumunda data URI göster
                                                    if (!e.target.dataset.errorHandled) {
                                                        e.target.dataset.errorHandled = 'true';
                                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjY3IiB2aWV3Qm94PSIwIDAgMTIwIDY3IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iNjciIGZpbGw9IiMxMTEiLz48dGV4dCB4PSI2MCIgeT0iMzQiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+QsO2bMO8bSBLYXBhxZ88L3RleHQ+PC9zdmc+';
                                                        e.target.onerror = null;
                                                    }
                                                }}
                                                />
                                            ) : (
                                                <img 
                                                    src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjY3IiB2aWV3Qm94PSIwIDAgMTIwIDY3IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iNjciIGZpbGw9IiMxMTEiLz48dGV4dCB4PSI2MCIgeT0iMzQiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+QsO2bMO8bSBLYXBhxZ88L3RleHQ+PC9zdmc+"
                                                    alt="Kapak yok"
                                                />
                                            )}
                                            <div className="thumbnail-info">
                                                <InputGroup>
                                                    <label>Bölüm Kapağı URL</label>
                                                    <input 
                                                        type="text"
                                                        placeholder="TMDB'den kapak URL'si..."
                                                        value={episode.thumbnail_path || ''}
                                                        onChange={(e) => handleEpisodeThumbnailChange(episode.id, e.target.value)}
                                                    />
                                                </InputGroup>
                                                <small>Örnek: /wRh5UvB6dlJdR5qC1K1j7JvZ7G.jpg</small>
                                            </div>
                                        </EpisodeThumbnail>
                                        
                                        <InputGroup>
                                            <label>Bölüm Adı</label>
                                            <input 
                                                type="text"
                                                placeholder="Bölüm adı..."
                                                value={episode.episode_title || ''}
                                                onChange={(e) => handleEpisodeChange(episode.id, 'episode_title', e.target.value)}
                                            />
                                        </InputGroup>
                                        
                                        <InputGroup>
                                            <label>Yayın Tarihi (Air Date)</label>
                                            <input 
                                                type="date"
                                                value={episode.air_date ? episode.air_date.split('T')[0] : ''}
                                                onChange={(e) => handleEpisodeChange(episode.id, 'air_date', e.target.value || null)}
                                            />
                                        </InputGroup>
                                        
                                        <InputGroup>
                                            <label>Bölüm Açıklaması</label>
                                            <textarea 
                                                placeholder="Bölüm açıklaması..."
                                                value={episode.episode_overview || ''}
                                                onChange={(e) => handleEpisodeChange(episode.id, 'episode_overview', e.target.value)}
                                            ></textarea>
                                        </InputGroup>
                                        
                                        <InputGroup>
                                            <label>Iframe Kodu</label>
                                            <textarea 
                                                placeholder={`<iframe>...</iframe> kodunu buraya yapıştır`}
                                                value={episode.iframe_code || ''}
                                                onChange={(e) => handleEpisodeChange(episode.id, 'iframe_code', e.target.value)}
                                            ></textarea>
                                        </InputGroup>

                                        {/* SEO Ayarları */}
                                        <SEOSection>
                                            <SEOTitle>SEO Ayarları</SEOTitle>
                                            
                                            <InputGroup>
                                                <label>SEO Başlık</label>
                                                <input 
                                                    type="text"
                                                    placeholder={`${content?.title || ''} Sezon ${activeSeason} Bölüm ${episode.episode_number} Türkçe İzle`}
                                                    value={episode.seo_title || ''}
                                                    onChange={(e) => handleEpisodeChange(episode.id, 'seo_title', e.target.value)}
                                                    maxLength={500}
                                                />
                                                <CharacterCount className={episode.seo_title?.length > 60 ? (episode.seo_title?.length > 70 ? 'error' : 'warning') : ''}>
                                                    {episode.seo_title?.length || 0}/500 karakter (Önerilen: 50-60 karakter)
                                                </CharacterCount>
                                            </InputGroup>

                                            <InputGroup>
                                                <label>SEO Açıklama</label>
                                                <textarea 
                                                    placeholder={`${content?.title || ''} Sezon ${activeSeason} Bölüm ${episode.episode_number} Türkçe dublaj ve altyazılı olarak izleyin...`}
                                                    value={episode.seo_description || ''}
                                                    onChange={(e) => handleEpisodeChange(episode.id, 'seo_description', e.target.value)}
                                                    rows={3}
                                                ></textarea>
                                                <CharacterCount className={episode.seo_description?.length > 160 ? (episode.seo_description?.length > 170 ? 'error' : 'warning') : ''}>
                                                    {episode.seo_description?.length || 0} karakter (Önerilen: 150-160 karakter)
                                                </CharacterCount>
                                            </InputGroup>

                                            <InputGroup>
                                                <label>SEO Anahtar Kelimeler</label>
                                                <input 
                                                    type="text"
                                                    placeholder={`${content?.title || ''} izle, ${content?.title || ''} sezon ${activeSeason} bölüm ${episode.episode_number} izle, ${content?.title || ''} türkçe izle`}
                                                    value={episode.seo_keywords || ''}
                                                    onChange={(e) => handleEpisodeChange(episode.id, 'seo_keywords', e.target.value)}
                                                />
                                                <CharacterCount>
                                                    Virgülle ayrılmış anahtar kelimeler
                                                </CharacterCount>
                                            </InputGroup>

                                            <InputGroup>
                                                <label>Canonical URL</label>
                                                <input 
                                                    type="text"
                                                    placeholder={`${DOMAIN}/watch/tv/${id}/season/${activeSeason}/episode/${episode.episode_number}`}
                                                    value={episode.seo_canonical_url || ''}
                                                    onChange={(e) => handleEpisodeChange(episode.id, 'seo_canonical_url', e.target.value)}
                                                />
                                            </InputGroup>

                                            {/* SEO Önizleme */}
                                            <SEOPreview>
                                                <div style={{ marginBottom: '10px', color: 'var(--gradient-cyan)', fontWeight: 600, fontSize: '0.9rem' }}>
                                                    Google Arama Önizlemesi
                                                </div>
                                                <PreviewTitle>
                                                    {episode.seo_title || `${content?.title || ''} Sezon ${activeSeason} Bölüm ${episode.episode_number} Türkçe İzle`}
                                                </PreviewTitle>
                                                <PreviewUrl>
                                                    {episode.seo_canonical_url || `${DOMAIN}/watch/tv/${id}/season/${activeSeason}/episode/${episode.episode_number}`}
                                                </PreviewUrl>
                                                <PreviewDescription>
                                                    {episode.seo_description || `${content?.title || ''} Sezon ${activeSeason} Bölüm ${episode.episode_number} Türkçe dublaj ve altyazılı olarak izleyin. ${episode.episode_overview ? episode.episode_overview.substring(0, 120) : ''} Ücretsiz ve HD kalitede.`}
                                                </PreviewDescription>
                                            </SEOPreview>
                                        </SEOSection>

                                        {/* Dil Seçenekleri - Sadece dublaj, altyazı veya fansub varsa göster */}
                                        {((episode.tr_dublaj !== undefined && episode.tr_dublaj !== null && episode.tr_dublaj.trim() !== '') ||
                                          (episode.tr_altyazi !== undefined && episode.tr_altyazi !== null && episode.tr_altyazi.trim() !== '') ||
                                          (episode.fansubs && episode.fansubs !== '{}' && episode.fansubs.trim() !== '')) && (
                                            <LanguageSection>
                                                <LanguageTitle>
                                                    <FaImage /> Dil Seçenekleri
                                                </LanguageTitle>
                                                
                                                <AddLanguageButton onClick={() => {
                                                    // TR Dublaj alanını aktif et
                                                    if (!episode.tr_dublaj || episode.tr_dublaj.trim() === '') {
                                                        handleEpisodeChange(episode.id, 'tr_dublaj', '');
                                                    }
                                                }}>
                                                    <FaPlus /> TR Dublaj Ekle
                                                </AddLanguageButton>
                                                {(episode.tr_dublaj !== undefined && episode.tr_dublaj !== null && episode.tr_dublaj.trim() !== '') && (
                                                    <LanguageInputGroup>
                                                        <input
                                                            type="text"
                                                            placeholder="TR Dublaj iframe kodu..."
                                                            value={episode.tr_dublaj || ''}
                                                            onChange={(e) => handleEpisodeChange(episode.id, 'tr_dublaj', e.target.value)}
                                                        />
                                                        <button onClick={() => handleEpisodeChange(episode.id, 'tr_dublaj', '')}>
                                                            <FaTrash />
                                                        </button>
                                                    </LanguageInputGroup>
                                                )}

                                                <AddLanguageButton onClick={() => {
                                                    // TR Altyazı alanını aktif et
                                                    if (!episode.tr_altyazi || episode.tr_altyazi.trim() === '') {
                                                        handleEpisodeChange(episode.id, 'tr_altyazi', '');
                                                    }
                                                }}>
                                                    <FaPlus /> TR Altyazı Ekle
                                                </AddLanguageButton>
                                                {(episode.tr_altyazi !== undefined && episode.tr_altyazi !== null && episode.tr_altyazi.trim() !== '') && (
                                                    <LanguageInputGroup>
                                                        <input
                                                            type="text"
                                                            placeholder="TR Altyazı iframe kodu..."
                                                            value={episode.tr_altyazi || ''}
                                                            onChange={(e) => handleEpisodeChange(episode.id, 'tr_altyazi', e.target.value)}
                                                        />
                                                        <button onClick={() => handleEpisodeChange(episode.id, 'tr_altyazi', '')}>
                                                            <FaTrash />
                                                        </button>
                                                    </LanguageInputGroup>
                                                )}

                                                <AddLanguageButton onClick={() => setShowFansubInput(!showFansubInput)}>
                                                    <FaPlus /> Fansub Ekle
                                                </AddLanguageButton>
                                                
                                                {showFansubInput && (
                                                    <FansubInputGroup>
                                                        <FansubRow>
                                                            <input
                                                                type="text"
                                                                placeholder="Fansub adı..."
                                                                value={newFansub.name}
                                                                onChange={(e) => setNewFansub(prev => ({...prev, name: e.target.value}))}
                                                                className="fansub-name"
                                                            />
                                                            <input
                                                                type="text"
                                                                placeholder="Fansub iframe kodu..."
                                                                value={newFansub.iframe}
                                                                onChange={(e) => setNewFansub(prev => ({...prev, iframe: e.target.value}))}
                                                                className="fansub-iframe"
                                                            />
                                                            <button onClick={() => {
                                                                if (newFansub.name && newFansub.iframe) {
                                                                    const fansubs = episode.fansubs ? JSON.parse(episode.fansubs) : {};
                                                                    fansubs[newFansub.name] = newFansub.iframe;
                                                                    handleEpisodeChange(episode.id, 'fansubs', JSON.stringify(fansubs));
                                                                    setNewFansub({ name: '', iframe: '' });
                                                                    setShowFansubInput(false);
                                                                }
                                                            }}>
                                                                Ekle
                                                            </button>
                                                        </FansubRow>
                                                    </FansubInputGroup>
                                                )}

                                                {episode.fansubs && episode.fansubs !== '{}' && episode.fansubs.trim() !== '' && Object.entries(JSON.parse(episode.fansubs)).map(([fansubName, fansubIframe]) => (
                                                    <LanguageInputGroup key={fansubName}>
                                                        <input
                                                            type="text"
                                                            placeholder="Fansub iframe kodu..."
                                                            value={fansubIframe}
                                                            onChange={(e) => {
                                                                const fansubs = JSON.parse(episode.fansubs);
                                                                fansubs[fansubName] = e.target.value;
                                                                handleEpisodeChange(episode.id, 'fansubs', JSON.stringify(fansubs));
                                                            }}
                                                        />
                                                        <button onClick={() => {
                                                            const fansubs = JSON.parse(episode.fansubs);
                                                            delete fansubs[fansubName];
                                                            const updatedFansubs = Object.keys(fansubs).length > 0 ? JSON.stringify(fansubs) : '{}';
                                                            handleEpisodeChange(episode.id, 'fansubs', updatedFansubs);
                                                        }}>
                                                            <FaTrash />
                                                        </button>
                                                    </LanguageInputGroup>
                                                ))}
                                            </LanguageSection>
                                        )}
                                        
                                        {/* Dil seçenekleri yoksa ekleme butonları göster */}
                                        {(!episode.tr_dublaj || episode.tr_dublaj.trim() === '') &&
                                         (!episode.tr_altyazi || episode.tr_altyazi.trim() === '') &&
                                         (!episode.fansubs || episode.fansubs === '{}' || episode.fansubs.trim() === '') && (
                                            <LanguageSection>
                                                <LanguageTitle>
                                                    <FaImage /> Dil Seçenekleri
                                                </LanguageTitle>
                                                
                                                <AddLanguageButton onClick={() => {
                                                    handleEpisodeChange(episode.id, 'tr_dublaj', '');
                                                }}>
                                                    <FaPlus /> TR Dublaj Ekle
                                                </AddLanguageButton>

                                                <AddLanguageButton onClick={() => {
                                                    handleEpisodeChange(episode.id, 'tr_altyazi', '');
                                                }}>
                                                    <FaPlus /> TR Altyazı Ekle
                                                </AddLanguageButton>

                                                <AddLanguageButton onClick={() => setShowFansubInput(!showFansubInput)}>
                                                    <FaPlus /> Fansub Ekle
                                                </AddLanguageButton>
                                                
                                                {showFansubInput && (
                                                    <FansubInputGroup>
                                                        <FansubRow>
                                                            <input
                                                                type="text"
                                                                placeholder="Fansub adı..."
                                                                value={newFansub.name}
                                                                onChange={(e) => setNewFansub(prev => ({...prev, name: e.target.value}))}
                                                                className="fansub-name"
                                                            />
                                                            <input
                                                                type="text"
                                                                placeholder="Fansub iframe kodu..."
                                                                value={newFansub.iframe}
                                                                onChange={(e) => setNewFansub(prev => ({...prev, iframe: e.target.value}))}
                                                                className="fansub-iframe"
                                                            />
                                                            <button onClick={() => {
                                                                if (newFansub.name && newFansub.iframe) {
                                                                    const fansubs = {};
                                                                    fansubs[newFansub.name] = newFansub.iframe;
                                                                    handleEpisodeChange(episode.id, 'fansubs', JSON.stringify(fansubs));
                                                                    setNewFansub({ name: '', iframe: '' });
                                                                    setShowFansubInput(false);
                                                                }
                                                            }}>
                                                                Ekle
                                                            </button>
                                                        </FansubRow>
                                                    </FansubInputGroup>
                                                )}
                                            </LanguageSection>
                                        )}
                                    </EpisodeForm>
                                );
                            })
                        )}
                    </div>
                </EpisodeManager>
            )}

            {/* Film için iframe ve dil seçenekleri - Basitleştirilmiş */}
            {content.media_type === 'movie' && (
                <Form>
                    <InputGroup>
                        <label>Film Iframe Kodu</label>
                        <textarea 
                            name="iframe_code"
                            placeholder="<iframe>...</iframe> kodunu buraya yapıştır"
                            value={content.iframe_code || ''}
                            onChange={handleInputChange}
                        ></textarea>
                    </InputGroup>
                    
                    {/* Film Dil Seçenekleri - Sadece dublaj veya altyazı varsa göster */}
                    {((content.tr_dublaj !== undefined && content.tr_dublaj !== null && content.tr_dublaj.trim() !== '') ||
                      (content.tr_altyazi !== undefined && content.tr_altyazi !== null && content.tr_altyazi.trim() !== '')) && (
                        <LanguageSection>
                            <LanguageTitle>
                                <FaImage /> Film Dil Seçenekleri
                            </LanguageTitle>
                            
                            <AddLanguageButton onClick={() => {
                                setContent(prev => ({ ...prev, tr_dublaj: prev.tr_dublaj || '' }));
                            }}>
                                <FaPlus /> TR Dublaj Ekle
                            </AddLanguageButton>
                            {(content.tr_dublaj !== undefined && content.tr_dublaj !== null && content.tr_dublaj.trim() !== '') && (
                                <LanguageInputGroup>
                                    <input
                                        type="text"
                                        placeholder="TR Dublaj iframe kodu..."
                                        value={content.tr_dublaj || ''}
                                        onChange={(e) => handleInputChange({target: {name: 'tr_dublaj', value: e.target.value}})}
                                    />
                                    <button onClick={() => handleInputChange({target: {name: 'tr_dublaj', value: ''}})}>
                                        <FaTrash />
                                    </button>
                                </LanguageInputGroup>
                            )}

                            <AddLanguageButton onClick={() => {
                                setContent(prev => ({ ...prev, tr_altyazi: prev.tr_altyazi || '' }));
                            }}>
                                <FaPlus /> TR Altyazı Ekle
                            </AddLanguageButton>
                            {(content.tr_altyazi !== undefined && content.tr_altyazi !== null && content.tr_altyazi.trim() !== '') && (
                                <LanguageInputGroup>
                                    <input
                                        type="text"
                                        placeholder="TR Altyazı iframe kodu..."
                                        value={content.tr_altyazi || ''}
                                        onChange={(e) => handleInputChange({target: {name: 'tr_altyazi', value: e.target.value}})}
                                    />
                                    <button onClick={() => handleInputChange({target: {name: 'tr_altyazi', value: ''}})}>
                                        <FaTrash />
                                    </button>
                                </LanguageInputGroup>
                            )}
                        </LanguageSection>
                    )}
                    
                    {/* Film dil seçenekleri yoksa ekleme butonları göster */}
                    {(!content.tr_dublaj || content.tr_dublaj.trim() === '') &&
                     (!content.tr_altyazi || content.tr_altyazi.trim() === '') && (
                        <LanguageSection>
                            <LanguageTitle>
                                <FaImage /> Film Dil Seçenekleri
                            </LanguageTitle>
                            
                            <AddLanguageButton onClick={() => {
                                setContent(prev => ({ ...prev, tr_dublaj: '' }));
                            }}>
                                <FaPlus /> TR Dublaj Ekle
                            </AddLanguageButton>

                            <AddLanguageButton onClick={() => {
                                setContent(prev => ({ ...prev, tr_altyazi: '' }));
                            }}>
                                <FaPlus /> TR Altyazı Ekle
                            </AddLanguageButton>
                        </LanguageSection>
                    )}
                </Form>
            )}
        </EditContainer>
    );
};

export default AdminEditPage;
